// API-Kosten tab (API_COSTS_KONTRAKT_FRANK.md §4): monthly cost overview from
// claude_api_costs.mjs summary. The aggregator picks the authoritative source
// (provider-API > local metering) — the UI only renders + labels it.
import type { MarketingDeps } from "./marketing.js";
import { wireMotorRun } from "./marketing-poll.js";

// Provider totals come as objects (metering) or bare numbers (provider-API),
// so every accessor tolerates both shapes.
type ProviderVal = number | { cost_usd?: number } | undefined;
interface CostDay { date: string; cost_usd: number; cost_eur?: number; requests?: number; providers: Record<string, ProviderVal> }
interface ProviderAuth {
  source: string; last_pulled: string | null;
  total_usd: number; total_eur: number;
  provider_totals: Record<string, ProviderVal>;
  days: CostDay[];
}
interface CostSummary {
  ok?: boolean; month: string; generated_at?: string; usd_to_eur?: number;
  authoritative: "provider-api" | "local-metering";
  provider_authoritative: ProviderAuth | null;
  metered_total_usd?: number; metered_total_eur?: number;
  total_usd: number; total_eur: number;
  fixed_costs?: Array<{ label: string; eur: number }>;
  fixed_monthly_eur?: number;
  grand_total_eur?: number;
  provider_totals: Record<string, ProviderVal>;
  days: CostDay[];
  available_months: string[];
  warning: string | null;
}

// Zwei Nachkommastellen (Uli 21.09.2026). Vier Stellen lasen sich wie Messwerte, nicht wie Geld —
// und standen neben den Euro-Werten, die schon immer zwei hatten.
// ⚠️ Betraege unter einem halben Cent wuerden beim Runden zu "0,00" und saehen dann aus, als
//    haette an dem Tag nichts gelaufen. Dafuer eine eigene Anzeige, damit "nichts" und
//    "fast nichts" unterscheidbar bleiben.
const zwei = (n: number): string =>
  (n ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd = (n: number): string => (n > 0 && n < 0.005 ? "$<0,01" : `$${zwei(n)}`);
const eur = (n: number): string => (n > 0 && n < 0.005 ? "<0,01 €" : `${zwei(n)} €`);
const provUsd = (v: ProviderVal): number => (typeof v === "number" ? v : v?.cost_usd ?? 0);

function renderHeadline(host: HTMLElement, d: CostSummary): void {
  host.textContent = "";
  const isReal = d.authoritative === "provider-api";

  const big = document.createElement("div");
  big.className = "om-cost-total";
  big.textContent = `${usd(d.total_usd)} · ${eur(d.total_eur)}`;
  host.appendChild(big);

  // Fixe Abokosten (Metricool etc.) + Gesamttotal inkl. Fixkosten
  const fixed = d.fixed_costs ?? [];
  if (fixed.length) {
    const fx = document.createElement("p");
    fx.className = "dim";
    fx.textContent = "Fixkosten/Monat: " + fixed.map((f) => `${f.label} ${eur(f.eur)}`).join(" · ");
    host.appendChild(fx);
    const grand = document.createElement("div");
    grand.className = "om-cost-total";
    grand.style.fontSize = "1.05rem";
    grand.style.color = "#e5484d";
    grand.style.fontWeight = "700";
    grand.textContent = `Gesamt inkl. Fixkosten: ${eur(d.grand_total_eur ?? (d.total_eur + (d.fixed_monthly_eur ?? 0)))}`;
    host.appendChild(grand);
  }

  const src = document.createElement("p");
  src.className = "dim";
  const last = d.provider_authoritative?.last_pulled;
  src.textContent = isReal
    ? `Quelle: echt abgerechnet (Provider-API)${last ? ` · Stand ${d.generated_at ? new Date(last).toLocaleString("de-DE") : last}` : ""} · Metering-Abgleich: ${usd(d.metered_total_usd ?? 0)}`
    : "Quelle: geschätzt (lokales Metering) — die Headline-Zahl basiert auf der Preis-Tabelle.";
  host.appendChild(src);

  if (!isReal) {
    const hint = document.createElement("p");
    hint.className = "inline-msg error";
    hint.textContent = "ℹ Für echte, abgerechnete Zahlen die Admin-Keys (ANTHROPIC_ADMIN_KEY / OPENAI_ADMIN_KEY) unter „Einstellungen“ hinterlegen.";
    host.appendChild(hint);
  }
  if (d.warning) {
    const w = document.createElement("p");
    w.className = "inline-msg error";
    w.textContent = `⚠️ ${d.warning}`;
    host.appendChild(w);
  }
}

function renderProviders(host: HTMLElement, d: CostSummary): void {
  host.textContent = "";
  const totals = d.provider_authoritative?.provider_totals ?? d.provider_totals ?? {};
  const cards = document.createElement("div");
  cards.className = "om-kpis";
  const LABELS: Record<string, string> = { anthropic: "Anthropic", openai: "OpenAI", fal: "fal.ai (Video)", heygen: "HeyGen (Video)", elevenlabs: "ElevenLabs (Stimme)" };
  // feste Reihenfolge zuerst, danach evtl. weitere Provider dynamisch
  const order = ["anthropic", "openai", "fal", "heygen", "elevenlabs"];
  const provs = [...order.filter((p) => p in totals), ...Object.keys(totals).filter((p) => !order.includes(p))];
  if (!provs.length) provs.push("anthropic", "openai");  // leere Ansicht: Basis-Provider zeigen
  for (const prov of provs) {
    const card = document.createElement("div");
    card.className = "om-kpi";
    const val = document.createElement("div");
    val.className = "om-kpi-val";
    val.textContent = usd(provUsd(totals[prov]));
    const lab = document.createElement("div");
    lab.className = "om-kpi-label";
    lab.textContent = LABELS[prov] ?? prov;
    card.appendChild(val);
    card.appendChild(lab);
    cards.appendChild(card);
  }
  host.appendChild(cards);
}

function renderDays(host: HTMLElement, d: CostSummary): void {
  host.textContent = "";
  // Gemeterte Tagesdaten = tagesaktuell + komplett + alle Provider (inkl. fal).
  // (Der autoritative Provider-Pull ist oft nur ein Stichtag → für die Tagesansicht ungeeignet.)
  const days = d.days ?? [];
  if (!days.length) {
    host.innerHTML = '<p class="dim">Noch keine Kostendaten in diesem Monat.</p>';
    return;
  }
  const LABELS: Record<string, string> = { anthropic: "Anthropic", openai: "OpenAI", fal: "fal.ai", heygen: "HeyGen", elevenlabs: "ElevenLabs" };
  const order = ["anthropic", "openai", "fal", "heygen", "elevenlabs"];
  const present = new Set<string>();
  for (const day of days) for (const p of Object.keys(day.providers ?? {})) present.add(p);
  const provs = [...order.filter((p) => present.has(p)), ...[...present].filter((p) => !order.includes(p))];

  const table = document.createElement("table");
  table.className = "om-table";
  const head = table.createTHead().insertRow();
  for (const label of ["Tag", ...provs.map((p) => LABELS[p] ?? p), "Summe USD", "Summe EUR"]) {
    const th = document.createElement("th");
    th.textContent = label;
    head.appendChild(th);
  }
  const tbody = table.createTBody();
  for (const day of days) {
    const tr = tbody.insertRow();
    tr.insertCell().textContent = day.date;
    for (const p of provs) tr.insertCell().textContent = usd(provUsd(day.providers?.[p]));
    tr.insertCell().textContent = usd(day.cost_usd);
    tr.insertCell().textContent = day.cost_eur != null ? eur(day.cost_eur) : "—";
  }
  host.appendChild(table);
}

export function initApiCostsTab(m: HTMLElement, deps: MarketingDeps): void {
  const monthSel = m.querySelector("[data-cost-month]") as HTMLSelectElement | null;
  const headline = m.querySelector("[data-cost-headline]") as HTMLElement | null;
  const providers = m.querySelector("[data-cost-providers]") as HTMLElement | null;
  const daysHost = m.querySelector("[data-cost-days]") as HTMLElement | null;
  if (!monthSel || !headline || !providers || !daysHost) return;

  let monthsLoaded = false;

  const load = async (month?: string): Promise<void> => {
    const q = month ? `?month=${encodeURIComponent(month)}` : "";
    const r = await deps.api<CostSummary>(`/api/marketing/api-costs${q}`);
    const d = r.data;
    if (!d) {
      headline.innerHTML = '<p class="inline-msg error">Kosten konnten nicht geladen werden.</p>';
      providers.textContent = ""; daysHost.textContent = "";
      return;
    }
    if (!monthsLoaded) {
      monthSel.textContent = "";
      const months = d.available_months.length ? d.available_months : [d.month];
      for (const mth of months) {
        const o = document.createElement("option");
        o.value = mth; o.textContent = mth;
        monthSel.appendChild(o);
      }
      monthSel.value = d.month;
      monthsLoaded = true;
    }
    renderHeadline(headline, d);
    renderProviders(providers, d);
    renderDays(daysHost, d);
  };

  monthSel.addEventListener("change", () => { void load(monthSel.value); });

  wireMotorRun({
    deps, modal: m,
    btn: m.querySelector('[data-action="run-cost-pull"]'),
    msg: m.querySelector('[data-msg="apicosts"]'),
    jobType: "api_costs_pull",
    runUrl: "/api/marketing/motors/api_costs_pull/run",
    onDone: () => load(monthSel.value || undefined),
  });

  void load();
}
